import { endedByDailyCutoff, type ItemResponse, type SessionResponse } from '@proerp/shared';
import { apiRequest, setAccessTokenProvider, setSessionEndedHandler } from './apiClient';

/**
 * Eigene Authentifizierung (ADR 0012): Access-Token (JWT) aus dem Login-Flow
 * (Passwort + TOTP-2FA) im localStorage; der apiClient hängt es an jede Anfrage.
 *
 * Die Sitzung endet nach 12 h **Ruhe** (ADR 0106): Wer arbeitet, verlängert sie
 * über `renewSession()`; wer nichts tut, wird abgemeldet. Der Taktgeber dazu ist
 * `useSessionWatch`, die Zeitrechnung steckt in `lib/session.ts`.
 */
const TOKEN_KEY = 'proerp.accessToken';

/**
 * Warum die letzte Sitzung endete — überlebt den Neuladen-Sprung zur Anmeldung.
 * `sessionStorage`, nicht `localStorage`: Der Hinweis gehört zu diesem Tab und
 * soll nicht Tage später in einem anderen Fenster auftauchen.
 */
const END_REASON_KEY = 'proerp.sessionEndReason';

/**
 * Warum die Sitzung endete — bestimmt den Satz auf der Anmeldeseite.
 * `idle` = 12 h Ruhe (ADR 0106) · `daily` = nächtlicher Schnitt um 3 Uhr (ADR 0107).
 */
export type SessionEndReason = 'idle' | 'daily';

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Beim App-Start einhängen — liefert ab dann das gespeicherte Token und meldet
 * ab, sobald der Server eine Anfrage mit Token mit 401 abweist.
 */
export function initAuth(): void {
  setAccessTokenProvider(async () => getStoredToken());
  // Ein 401 ist kein normaler Ablauf (Passwortwechsel, deaktiviertes Konto) —
  // der Grund wird trotzdem am Token abgelesen, falls es noch da ist.
  setSessionEndedHandler(() => endSession(currentEndReason()));
}

/**
 * Grund aus dem gespeicherten Token ableiten: Wurde es auf weniger als das
 * volle Leerlauf-Fenster ausgestellt, hat der nächtliche Schnitt es gekürzt.
 * Sonst war es die Ruhe.
 */
export function currentEndReason(): SessionEndReason {
  return endedByDailyCutoff(getStoredToken()) ? 'daily' : 'idle';
}

export function logout(): void {
  clearToken();
  window.location.assign('/');
}

/**
 * Sitzung beenden, weil sie vorbei ist (nicht, weil jemand auf „Abmelden"
 * geklickt hat). Merkt den Grund für die Anmeldeseite, damit dort nicht
 * kommentarlos das Login-Formular steht — „ich war doch angemeldet?" ist genau
 * die Frage, die ein Auto-Logout erzeugt, wenn er nichts sagt.
 *
 * Mehrfachaufrufe sind unschädlich: Ist das Token schon weg, passiert nichts —
 * sonst würde ein Schwung paralleler 401-Antworten die Seite mehrfach neu laden.
 */
export function endSession(reason: SessionEndReason): void {
  if (!getStoredToken()) return;
  clearToken();
  try {
    sessionStorage.setItem(END_REASON_KEY, reason);
  } catch {
    // Ohne sessionStorage (privater Modus) fehlt nur der Hinweistext.
  }
  window.location.assign('/');
}

/**
 * Beim ersten Lesen aus dem `sessionStorage` geholt und dort gelöscht, danach
 * für diesen Seitenaufbau gemerkt. Warum gemerkt statt „einmal verbrauchen":
 * React ruft im StrictMode sowohl Effekte ALS AUCH `useState`-Initialisierer
 * doppelt auf — ein verbrauchendes Lesen liefert beim zweiten Aufruf `null`,
 * und genau der landet im Zustand (Vorfall 2026-09-24, der Hinweis fehlte auf
 * der Anmeldeseite).
 *
 * „Warum bin ich hier?" ist ohnehin eine Eigenschaft dieses Seitenaufbaus, nicht
 * des einzelnen Aufrufs. Beim nächsten Laden ist der Speicher leer.
 */
let rememberedReason: SessionEndReason | null | undefined;

export function takeSessionEndReason(): SessionEndReason | null {
  if (rememberedReason === undefined) {
    rememberedReason = readAndClearEndReason();
  }
  return rememberedReason;
}

function readAndClearEndReason(): SessionEndReason | null {
  try {
    const reason = sessionStorage.getItem(END_REASON_KEY);
    if (reason) sessionStorage.removeItem(END_REASON_KEY);
    return reason === 'idle' || reason === 'daily' ? reason : null;
  } catch {
    return null;
  }
}

/**
 * Sitzung verlängern: frisches Token holen und speichern. Wird bei Aktivität
 * gerufen, wenn das aktuelle Token alt genug ist (`shouldRenewSession`).
 *
 * Schlägt der Aufruf fehl, passiert bewusst nichts: Bei einem 401 hat das
 * Auffangnetz im apiClient schon abgemeldet, bei einem Netzfehler wäre ein
 * Rauswurf falsch — es bleiben ja noch Stunden, und der nächste Klick versucht
 * es erneut.
 */
export async function renewSession(): Promise<void> {
  try {
    const response = await apiRequest<ItemResponse<SessionResponse>>('/v1/auth/session/refresh', {
      method: 'POST',
    });
    storeToken(response.data.accessToken);
  } catch {
    // siehe oben — kein Rauswurf aus dem Verlängerungs-Pfad
  }
}

/** Freundlicher Geräte-Anzeigename für einen neu registrierten Passkey (ADR 0034). */
export function guessDeviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Android/.test(ua)) return 'Android-Gerät';
  if (/Windows/.test(ua)) return 'Windows-PC';
  return 'Mein Gerät';
}
